import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  digitalLeadsFormLoad,
  digitalLeadsFormSubmissionSuccess,
  digitalLeadsFormSubmissionError,
} from '../utils/analyticsEventListeners';
import { Form, Promotion } from '../types/analytics';
import { VehicleDataType, Vehicle, StockCarTypes } from '../types';
import { selectForm, setForm } from '../redux/formReducer';
import { FEATURE_APP_DETAILS } from '../constants';
import { IS_LOCAL_ENVIRONMENT } from '../constants/environment';

interface FormLoadProps {
  isModalOpen: boolean;
  leadSource: string;
  vehicle: any;
}

interface FormSubmissionSuccessProps {
  contactForm: Form;
  leadId: string;
  vehicle: any;
  promotion: Promotion | undefined;
}

interface FormSubmissionErrorProps {
  error: boolean;
  errorMessage: { code: number; message: string };
  contactForm: Form;
  vehicle: any;
  promotion: Promotion | undefined;
}

export const setUpdatedVTPLeadForm = (contactForm: Form, vehicle: StockCarTypes): Form => {
  const updatedForm = {
    ...contactForm,
    ...{
      formName: 'VTP Lead',
      formType: 'lead',
      formData: {
        ...contactForm.formData,
        ...{
          preferredDealer: vehicle?.dealer?.id,
          modelOfInterest: { modelFamily: vehicle?.titleText },
        },
      },
    },
  };
  return updatedForm;
};

const setUpdatedGenericLeadForm = (contactForm: Form): Form => {
  const updatedForm = {
    ...contactForm,
    ...{
      formName: 'Generic Lead',
      formType: 'lead',
      formData: {
        ...contactForm.formData,
      },
    },
  };
  return updatedForm;
};

export const setUpdatedPromotionsLeadForm = (
  contactForm: Form,
  dealerId: string,
  modelName: string,
): Form => {
  const updatedForm = {
    ...contactForm,
    ...{
      formName: 'Generic Lead',
      formType: 'lead',
      formData: {
        ...contactForm.formData,
        ...{
          preferredDealer: dealerId,
          modelOfInterest: { modelFamily: modelName },
        },
      },
    },
  };
  return updatedForm;
};

export const useGenericFormLoad = ({ isModalOpen, leadSource, vehicle }: FormLoadProps) => {
  const dispatch = useDispatch();
  const contactForm = useSelector(selectForm);

  const isGeneralLeadSource = leadSource === 'general' || leadSource === 'generic';
  // DEV: for testing VTP Lead - switch IS_LOCAL_ENVIRONMENT to !IS_LOCAL_ENVIRONMENT
  const vehicleNotRequired = leadSource === '' || isGeneralLeadSource || IS_LOCAL_ENVIRONMENT;

  useEffect(() => {
    let updatedForm = {} as Form;

    if (
      (contactForm.formName === '' || contactForm.formName === 'Generic Lead') &&
      !vehicle &&
      vehicleNotRequired
    ) {
      updatedForm = setUpdatedGenericLeadForm(contactForm);
    }

    if (isModalOpen && updatedForm?.formName && isGeneralLeadSource) {
      dispatch(setForm(updatedForm));
      digitalLeadsFormLoad(updatedForm, vehicle, FEATURE_APP_DETAILS);
    }
    // contactForm causes infinate recursion
    // eslint-disable-next-line
  }, [isModalOpen, dispatch, vehicle, vehicleNotRequired]);
};

export const useSuccessfulFormSubmission = ({
  contactForm,
  leadId,
  vehicle,
  promotion,
}: FormSubmissionSuccessProps) => {
  useEffect(() => {
    if (leadId) {
      const updatedForm = {
        ...contactForm,
        ...{
          leadId,
        },
      };
      digitalLeadsFormSubmissionSuccess(updatedForm, vehicle!, FEATURE_APP_DETAILS, promotion);
    }
  }, [contactForm, leadId, vehicle, promotion]);
};

export const useUnsuccessfulFormError = ({
  error,
  errorMessage,
  contactForm,
  vehicle,
  promotion,
}: FormSubmissionErrorProps) => {
  useEffect(() => {
    if (error && errorMessage?.message?.length > 0) {
      const errorPayload = {
        error_message: errorMessage?.message,
        error_code: errorMessage?.code,
      };

      digitalLeadsFormSubmissionError(
        contactForm,
        vehicle!,
        FEATURE_APP_DETAILS,
        promotion,
        errorPayload,
      );
    }
  }, [contactForm, error, errorMessage, vehicle, promotion]);
};
